/* [VALE 2026-08-20] Tabbed ("IKEA style") mega menu.
   Two custom elements:
     <header-menu-click>  click-to-open replacement for <header-menu> (which opens on hover)
     <mega-tabs>          the tab strip + panels inside the open panel
   Used by the "Tabbed mega menu" header block. Remove the block to stop using them. */

class HeaderMenuClick extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    if (!this.details || !this.summary) return;

    this.header = document.querySelector('.header-wrapper');

    this.onSummaryClick = this.handleSummaryClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeyUp = this.handleKeyUp.bind(this);

    this.summary.addEventListener('click', this.onSummaryClick);
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keyup', this.onKeyUp);

    if (window.Shopify && Shopify.designMode) {
      this.onBlockSelect = () => this.open();
      this.onBlockDeselect = () => this.close();
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /*
      Open on hover as well as click, so the tabbed menu behaves like the other
      mega menus. Click is kept: it is the only way in on touch devices, which do
      not match (hover: hover). Leaving the element closes it, and because the
      panel is a DOM descendant, moving the pointer into the panel does not count
      as leaving.
    */
    if (window.matchMedia('(hover: hover)').matches) {
      this.onMouseEnter = () => {
        clearTimeout(this.hoverTimer);
        const delay = typeof window.menuHoverIntentDelay === 'number' ? window.menuHoverIntentDelay : 100;
        this.hoverTimer = setTimeout(() => this.open(), delay);
      };
      this.onMouseLeave = () => {
        clearTimeout(this.hoverTimer);
        this.close();
      };
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.hoverTimer);
    if (this.onMouseEnter) {
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
    }
    this.summary?.removeEventListener('click', this.onSummaryClick);
    document.removeEventListener('click', this.onDocumentClick);
    this.removeEventListener('keyup', this.onKeyUp);
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  handleSummaryClick(event) {
    // The panel is opened by the click, so the top-level link itself never navigates.
    event.preventDefault();
    this.details.open ? this.close() : this.open();
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target) && this.details.open) this.close();
  }

  handleKeyUp(event) {
    if (event.key !== 'Escape' || !this.details.open) return;
    this.close();
    this.summary.focus();
  }

  open() {
    if (!this.details || this.details.open) return;

    // close anything else that is open, hover menus included
    document.querySelectorAll('header-menu > details[open]').forEach((openDetails) => {
      const headerMenu = openDetails.closest('header-menu');
      if (headerMenu && headerMenu.summary) headerMenu.summary.click();
    });
    document.querySelectorAll('header-menu-click').forEach((menu) => {
      if (menu !== this) menu.close();
    });

    this.classList.add('open');
    this.style.setProperty(
      '--link-end',
      `${Number.parseInt(this.offsetTop + this.getBoundingClientRect().height) - 1}px`
    );
    this.style.setProperty('--header-zindex', '1');

    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');

    if (this.header) {
      this.header.preventHide = true;
      if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === '') {
        document.documentElement.style.setProperty(
          '--header-bottom-position-desktop',
          `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
        );
      }
    }
  }

  close() {
    if (!this.details || !this.details.open) return;
    this.details.open = false;
    this.summary.setAttribute('aria-expanded', 'false');
    this.classList.remove('open');
    this.style.setProperty('--header-zindex', '0');
    if (this.header) this.header.preventHide = false;
  }
}

if (!customElements.get('header-menu-click')) {
  customElements.define('header-menu-click', HeaderMenuClick);
}

class MegaTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.strip = this.querySelector('.mtabs__strip');
    this.prev = this.querySelector('.mtabs__arrow--prev');
    this.next = this.querySelector('.mtabs__arrow--next');
    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
    });

    if (this.strip) {
      this.onScroll = this.updateArrows.bind(this);
      this.strip.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.prev?.addEventListener('click', () => this.scrollStrip(-1));
      this.next?.addEventListener('click', () => this.scrollStrip(1));
      // the strip has no width until the panel opens, so measure then too
      this.observer = new ResizeObserver(() => this.updateArrows());
      this.observer.observe(this.strip);
      this.updateArrows();
    }
  }

  disconnectedCallback() {
    if (this.onScroll) {
      this.strip?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    this.observer?.disconnect();
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });
    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
    this.tabs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  handleKeydown(event, index) {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(index);
      return;
    }
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
    this.select(next);
    this.tabs[next].focus();
  }

  scrollStrip(direction) {
    if (!this.strip) return;
    this.strip.scrollBy({ left: direction * this.strip.clientWidth * 0.7, behavior: 'smooth' });
  }

  updateArrows() {
    if (!this.strip || !this.prev || !this.next) return;
    const overflowing = this.strip.scrollWidth - this.strip.clientWidth > 2;
    const atStart = this.strip.scrollLeft <= 2;
    const atEnd = this.strip.scrollLeft + this.strip.clientWidth >= this.strip.scrollWidth - 2;
    this.prev.hidden = !overflowing || atStart;
    this.next.hidden = !overflowing || atEnd;
  }
}

if (!customElements.get('mega-tabs')) {
  customElements.define('mega-tabs', MegaTabs);
}
